import { createAccountHeaderClickEvent } from '@/domain/header/analytics'
import { useFavouriteProductIdsQuery } from '@/domain/product/hooks'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import TopNavigationBarItem from '@/scopes/common/components/Header/TopNavigationBarItem'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { space, tok, tokPx, zIndex } from '@sunwebgroup/ui-components'
import {
  Button,
  Icon,
  type IconGlyph,
  MenuItem,
  MenuLinkItem,
  Popover,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'

interface MenuItemProps {
  href?: string
  onClick?: () => void
  icon?: IconGlyph
  labelId: string
  rightText?: number
}

const Trigger = styled(TopNavigationBarItem)`
  gap: ${space('primitives_space_4')};
  padding-left: ${space('primitives_space_8')};
  padding-right: ${space('primitives_space_8')};
  min-width: 40px;
  justify-content: center;

  ${Typography} {
    display: none;
  }

  &[data-state='open'] {
    background: ${tok('primitives_color_neutral_150')};
  }

  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
      ${Typography} {
        display: inline;
      }
    }
`

AccountMenu.RegisterLink = styled(Button)``

AccountMenu.Avatar = styled.div`
  font-family: ${tok('primitives_font_family_display')};
  font-weight: 700;
  font-size: 14px;
  align-items: center;
  background: ${tok('primitives_color_primary_500')};
  border-radius: 50%;
  color: ${tok('primitives_color_neutral_0')};
  display: flex;
  height: ${space('primitives_space_20')};
  justify-content: center;
  width: ${space('primitives_space_20')};
`

AccountMenu.DropdownMenuLink = styled.a`
    padding: ${space('primitives_space_8')};
  text-decoration: none;

`

AccountMenu.DropdownMenuButton = styled.button`
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  display: block;
  padding: ${space('primitives_space_12')} ${space('primitives_space_24')};
  text-align: left;
  width: 100%;

  &:hover {
    background: ${tok('primitives_color_neutral_100')};
  }
`

AccountMenu.DropdownMenuItem = styled.div`
  width: 100%;
`

AccountMenu.DropdownMenuDivider = styled.div`
  background: ${tok('semantics_dividers_color')};
  height: 2px;
  margin-bottom: ${space('primitives_space_6')};
`

AccountMenu.LoginButton = styled(Button)`
  margin-bottom: ${space('primitives_space_8')};
`

AccountMenu.UpperLogoutContainer = styled.div`
  margin-bottom:${space('primitives_space_12')};
  display: flex;
  flex-direction: column;
`
const TitleUpperLogoutWrapper = styled.div`
  margin-bottom: ${space('primitives_space_12')};
    `

AccountMenu.TitleUpperLogout = styled(Typography)``

const PopoverContent = styled(Popover.Content)`
 z-index: ${zIndex.modalOrMobilePopover};
`

const DropdownMenuDividerLogged = styled(AccountMenu.DropdownMenuDivider)`
  margin: ${space('primitives_space_8')} 0;
`

const AccountMenuItem: React.FC<MenuItemProps> = ({ href, onClick, icon, labelId, rightText }) => {
  const intl = useIntl()

  return (
    <AccountMenu.DropdownMenuItem>
      {href
        ? (
            <MenuLinkItem glyph={icon} href={href} onClick={onClick} rightSlot={rightText?.toString()}>
              {intl.formatMessage({ id: labelId })}
            </MenuLinkItem>
          )
        : (
            <MenuItem onClick={onClick}>
              {intl.formatMessage({ id: labelId })}
            </MenuItem>
          )}
    </AccountMenu.DropdownMenuItem>
  )
}

const LoggedOutMenu: React.FC = () => {
  const intl = useIntl()
  const links = useConfiguration('links')
  const { sendEvent } = useAnalytics()
  const { isSunwebCE, logout } = useUser()
  const { data: favourites } = useFavouriteProductIdsQuery()
  const track = (result: string, description: string) => () => {
    sendEvent(createAccountHeaderClickEvent({ result, description }))
  }

  return (
    <>
      <AccountMenu.UpperLogoutContainer>
        <TitleUpperLogoutWrapper>
          <AccountMenu.TitleUpperLogout $variant="display-h-regular">
            {intl.formatMessage({ id: 'header_dropdown_menu_title' })}
          </AccountMenu.TitleUpperLogout>
        </TitleUpperLogoutWrapper>
        <AccountMenu.LoginButton
          as="a"
          href={links.login}
          $fullWidth
          onClick={track('Click on My Sunweb', 'redirects to the login page')}
        >
          {intl.formatMessage({ id: 'header_dropdown_menu_login' })}
        </AccountMenu.LoginButton>
        <AccountMenu.RegisterLink
          href={links.createAccount}
          as="a"
          $variant="secondary"
          $fullWidth
          onClick={track('Click on Create Account', 'redirects to the account creation page')}
        >
          <Typography>{intl.formatMessage({ id: 'header_dropdown_menu_create_account' })}</Typography>
        </AccountMenu.RegisterLink>
      </AccountMenu.UpperLogoutContainer>

      <AccountMenu.DropdownMenuDivider />
      {!isSunwebCE && (
        <>
          <AccountMenuItem
            href={links.myPortal}
            icon="Suitcase"
            labelId="header_my_bookings"
            onClick={track('Click on My Bookings', 'redirects to \'My Sunweb\' page')}
          />
          <AccountMenuItem
            href={links.favourites}
            icon="Heart"
            labelId="header_my_favourites"
            rightText={favourites?.length ?? 0}
            onClick={track('Click on Favorites', 'redirects to the favorites page')}
          />
        </>
      )}
      {links.loginCE
      && (isSunwebCE
        ? (
            <AccountMenuItem onClick={logout} labelId="header_dropdown_menu_logout_ce" />
          )
        : (
            <AccountMenuItem href={links.loginCE} icon="TicketWithPercentage" labelId="header_login_ce" />
          ))}
    </>
  )
}

const LoggedInMenu: React.FC = () => {
  const links = useConfiguration('links')
  const { data: favourites } = useFavouriteProductIdsQuery()
  const { logout } = useUser()
  const items: MenuItemProps[] = [
    { href: links.myPortal, icon: 'Suitcase', labelId: 'header_my_bookings' },
    { href: links.favourites, icon: 'Heart', labelId: 'header_my_favourites', rightText: favourites?.length },
  ]
  if (links.loginCE) {
    items.push({ href: links.loginCE, icon: 'TicketWithPercentage', labelId: 'header_login_ce' })
  }

  return (
    <>
      {items.map((item, i) => (
        <AccountMenuItem key={i} {...item} />
      ))}
      <DropdownMenuDividerLogged />
      <AccountMenuItem href={links.myProfile} labelId="header_my_profile" />
      <DropdownMenuDividerLogged />
      <AccountMenuItem onClick={logout} labelId="header_dropdown_menu_logout" />
    </>
  )
}

export function AccountMenu() {
  const intl = useIntl()
  const links = useConfiguration('links')
  const { capitalLetter, isLoggedIn, isSunwebCE } = useUser()
  const showLoggedIn = isLoggedIn && (!links.loginCE || !isSunwebCE)

  const TriggerIcon
    = isLoggedIn && capitalLetter
      ? (
          <AccountMenu.Avatar>{capitalLetter}</AccountMenu.Avatar>
        )
      : (
          <Icon glyph="PersonInCircle" />
        )

  return (
    <Popover.Root>
      <Popover.Trigger>
        <Trigger>
          {TriggerIcon}
          <Typography>{intl.formatMessage({ id: 'my_portal' })}</Typography>
        </Trigger>
      </Popover.Trigger>
      <PopoverContent position="bottom-left" title={intl.formatMessage({ id: 'my_portal' })}>
        {showLoggedIn ? <LoggedInMenu /> : <LoggedOutMenu />}
      </PopoverContent>
    </Popover.Root>
  )
}
